import React, { useEffect, useState, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, CheckCircle, Lightning, Star, ChartLineUp, Brain, Calculator, ChartBar } from "@phosphor-icons/react";

const STORAGE_KEY = "bs.onboarding.v2";

const STEPS = [
  {
    target: '[data-testid="sidebar-tab-signals"]',
    title: "Step 1 · Breakouts tab",
    body: "Left sidebar se Breakouts tab par click karein — yahaan saare active breakouts row-by-row dikhte hain. Har row pe symbol · score · price · today % · entry · SL · target ek line me. Volume + 6-month/2-year high cross strong setups.",
    icon: Lightning,
  },
  {
    target: '[data-testid^="breakout-row-"]',
    title: "Step 2 · Row click → Full chart",
    body: "Kisi bhi row par click karein → full chart modal khulta hai with TradingView chart, AI Fundamental check, Position Sizer aur exit rules. Close button (X) top-right me.",
    icon: ChartLineUp,
  },
  {
    target: '[data-testid="sidebar-tab-watchlist"]',
    title: "Step 3 · Watchlist + alerts",
    body: "Star kisi bhi breakout pe to track karne ke liye → Watchlist me LTP live refresh hoga. Top-bar me ✉️ icon click karke email alerts ON karo — Target/SL hit pe inbox me notification milegi.",
    icon: Star,
  },
  {
    target: 'body',
    title: "Step 4 · AI Chart Analysis",
    body: "Stock modal khulne ke baad, right side ka 'Analyze Chart' button press karein. Gemini AI 30 seconds me bias (bullish/bearish), support/resistance levels, RSI/MACD signals, aur volume read deta hai. Levels chart pe pin bhi kar sakte ho.",
    icon: Brain,
  },
  {
    target: 'body',
    title: "Step 5 · Risk Calculator",
    body: "Trade place karne se pehle Top bar me 'Risk Calc' link click karein. Entry / SL / Target + capital + risk % daalo — exact quantity, total risk ₹ aur R:R ratio milta hai. Trade setup ko share-as-image bhi kar sakte ho.",
    icon: Calculator,
  },
  {
    target: 'body',
    title: "Step 6 · Track Record",
    body: "Login page ya 'Track Record' link pe live performance dekho — 30D / 3M / 6M / 1Y windows mein scanner ki actual win rate, profit factor aur NIFTY 50 comparison. Real closed signals, koi back-test nahi.",
    icon: ChartBar,
  },
];

function getRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { el, rect: r };
}

export default function OnboardingTour({ shouldStart, onClose, forceStart = false }) {
  const [step, setStep] = useState(-1); // -1 = not started, 0..N-1 = active, N = finished
  const [pos, setPos] = useState(null);

  // Auto-start once when shouldStart flips true (respects "done" flag, unless forceStart)
  useEffect(() => {
    if (!shouldStart) return;
    if (!forceStart && localStorage.getItem(STORAGE_KEY) === "done") return;
    setStep(0);
  }, [shouldStart, forceStart]);

  // Recompute target rect on step change / window resize / scroll
  useLayoutEffect(() => {
    if (step < 0 || step >= STEPS.length) {
      setPos(null);
      return;
    }
    const update = () => {
      const found = getRect(STEPS[step].target);
      if (!found) {
        // Target not yet rendered — retry on next frame
        return;
      }
      // Scroll into view if mostly off-screen (skip for body — full-page steps)
      const r = found.rect;
      if (STEPS[step].target !== "body" && (r.top < 60 || r.bottom > window.innerHeight - 60)) {
        found.el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Re-read after scroll
      requestAnimationFrame(() => {
        const next = getRect(STEPS[step].target);
        if (next) setPos(next.rect);
      });
    };
    update();
    const t = setInterval(update, 600); // keep tracking while visible (handles re-renders)
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [step]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "done");
    setStep(-1);
    onClose?.();
  };

  const next = () => {
    if (step >= STEPS.length - 1) {
      localStorage.setItem(STORAGE_KEY, "done");
      setStep(STEPS.length); // show "all done" briefly
      setTimeout(() => {
        setStep(-1);
        onClose?.();
      }, 1400);
      return;
    }
    setStep((s) => s + 1);
  };

  if (step < 0) return null;

  const isFinished = step >= STEPS.length;
  const cur = STEPS[step] || STEPS[STEPS.length - 1];
  const Icon = cur.icon;
  // For full-page steps ("body" target), centre the popover and skip the highlight ring.
  const isFullPage = cur?.target === "body";

  // Compute popover position (default below the target, fallback above).
  // For full-page steps (target=body) center it on screen.
  let popStyle = { top: 80, left: 24 };
  if (isFullPage) {
    popStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  } else if (pos) {
    const margin = 12;
    const popW = 360;
    const popH = 220;
    const placeBelow = pos.bottom + popH + margin < window.innerHeight;
    const top = placeBelow ? pos.bottom + margin : pos.top - popH - margin;
    let left = pos.left + pos.width / 2 - popW / 2;
    left = Math.max(16, Math.min(window.innerWidth - popW - 16, left));
    popStyle = { top: Math.max(16, top), left };
  }

  return (
    <AnimatePresence>
      {/* Dim backdrop with cutout-effect via 4 quadrants around the target */}
      <motion.div
        key="tour-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] pointer-events-none"
        data-testid="onboarding-overlay"
      >
        <div className="absolute inset-0 bg-black/65 backdrop-blur-[2px] pointer-events-auto" onClick={dismiss} />
        {pos && !isFinished && !isFullPage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="absolute pointer-events-none ring-2 ring-pos rounded-sm shadow-[0_0_0_9999px_rgba(0,0,0,0.65)]"
            style={{
              top: pos.top - 6,
              left: pos.left - 6,
              width: pos.width + 12,
              height: pos.height + 12,
            }}
          />
        )}

        {/* Popover */}
        {!isFinished ? (
          <motion.div
            key={`tour-step-${step}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="absolute pointer-events-auto bg-surface border border-pos rounded-sm shadow-2xl shadow-pos/20 p-5"
            style={{ ...popStyle, width: 360, maxWidth: "calc(100vw - 32px)" }}
            data-testid={`onboarding-step-${step}`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-sm bg-pos-soft border border-pos-soft flex items-center justify-center">
                  <Icon size={15} weight="bold" className="text-pos" />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-pos">
                    /// Quick start · {step + 1}/{STEPS.length}
                  </div>
                  <div className="font-medium text-primary-fg text-sm">{cur.title}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={dismiss}
                data-testid="onboarding-close"
                className="p-1 text-muted-fg hover:text-primary-fg hover:bg-surface-hover rounded-sm"
              >
                <X size={14} />
              </button>
            </div>
            <p className="text-sm text-secondary-fg leading-relaxed mb-5">{cur.body}</p>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={dismiss}
                data-testid="onboarding-skip"
                className="text-xs font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg"
              >
                Skip tour
              </button>
              <button
                type="button"
                onClick={next}
                data-testid="onboarding-next"
                className="px-4 py-2 rounded-sm text-xs font-medium flex items-center gap-2"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                {step === STEPS.length - 1 ? "Got it" : "Next"}
                <ArrowRight size={12} weight="bold" />
              </button>
            </div>
            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mt-4">
              {STEPS.map((s, i) => (
                <span
                  key={s.target}
                  className={
                    "h-1 rounded-full transition-all " +
                    (i === step ? "w-6 bg-pos" : i < step ? "w-3 bg-pos/60" : "w-3 bg-subtle")
                  }
                />
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="tour-done"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute pointer-events-auto bg-surface border border-pos rounded-sm shadow-2xl p-6 flex flex-col items-center text-center"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 320,
            }}
            data-testid="onboarding-done"
          >
            <CheckCircle size={32} weight="fill" className="text-pos mb-2" />
            <div className="font-medium text-primary-fg text-base">You're all set 🎉</div>
            <div className="text-xs text-muted-fg mt-1">Happy scanning!</div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
